import type { MapElementType } from '@/lib/shared-types';

/**
 * ADR-005 / §5.2 — the floor map is one vector document, so it is read in one place.
 *
 * The dashboard editor, the dashboard's live view and the waiter app all draw the *same* scene
 * from `GET /floor-map`, and each had its own copy of this reader: two readers of one document
 * eventually disagree, and then a room looks different depending on which screen you stand at.
 *
 * Structure only. Rendering differs by platform (SVG on the web, `react-native-svg` on the phone)
 * and colour is presentation, so the palette lives in `@/lib/ui-tokens`.
 */

export type MapEntityType = 'VenueTable' | 'Resource';

export interface SceneElement {
  /** Server id, or a `local:` id for an element that has never been saved. */
  id: string;
  elementType: MapElementType;
  entityType: MapEntityType | null;
  entityId: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
  locked: boolean;
  visible: boolean;
  /** Grouping has no column of its own; it rides along in `metadataJson`. */
  groupId: string | null;
  label: string | null;
  styleJson: Record<string, unknown> | null;
  metadataJson: Record<string, unknown> | null;
}

export interface Scene {
  floorId: string;
  name: string;
  width: number;
  height: number;
  elements: readonly SceneElement[];
}

export interface ServerMapElement {
  id: string;
  floorId: string;
  elementType: MapElementType;
  entityType: string | null;
  entityId: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  zIndex: number;
  locked: boolean;
  visible: boolean;
  styleJson: Record<string, unknown> | null;
  metadataJson: Record<string, unknown> | null;
}

export interface FloorScene {
  id: string;
  branchId: string;
  name: string;
  level: number;
  width: number;
  height: number;
  mapElements: ServerMapElement[];
}

export const DEFAULT_FLOOR_WIDTH = 1200;
export const DEFAULT_FLOOR_HEIGHT = 800;

function readMeta(meta: Record<string, unknown> | null, key: string): string | null {
  const value = meta?.[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function elementFromServer(row: ServerMapElement): SceneElement {
  return {
    id: row.id,
    elementType: row.elementType,
    entityType: (row.entityType as MapEntityType | null) ?? null,
    entityId: row.entityId ?? null,
    x: row.x,
    y: row.y,
    width: row.width,
    height: row.height,
    rotation: row.rotation,
    zIndex: row.zIndex,
    locked: row.locked,
    visible: row.visible,
    groupId: readMeta(row.metadataJson, 'groupId'),
    label: readMeta(row.metadataJson, 'label'),
    styleJson: row.styleJson ?? null,
    metadataJson: row.metadataJson ?? null,
  };
}

export function sceneFromFloor(floor: FloorScene): Scene {
  return {
    floorId: floor.id,
    name: floor.name,
    width: floor.width || DEFAULT_FLOOR_WIDTH,
    height: floor.height || DEFAULT_FLOOR_HEIGHT,
    elements: [...(floor.mapElements ?? [])]
      .sort((a, b) => a.zIndex - b.zIndex)
      .map(elementFromServer),
  };
}

/** Corner rounding, so a table reads as a table and a wall reads as a wall. */
export function cornerRadius(type: MapElementType): number {
  if (type === 'TABLE') return 14;
  if (type === 'RESOURCE' || type === 'CUSTOM' || type === 'TEXT') return 8;
  return 2;
}

export function centerOf(element: { x: number; y: number; width: number; height: number }): {
  x: number;
  y: number;
} {
  return { x: element.x + element.width / 2, y: element.y + element.height / 2 };
}

/**
 * §38 — an element that stands for a real table, as opposed to scenery. Both halves matter:
 * `entityType` says what kind of thing it points at, `entityId` whether it points at one at all.
 * A TABLE element that was never linked is furniture, not something to act on.
 */
export function isLinkedTable(element: SceneElement): boolean {
  return element.entityType === 'VenueTable' && !!element.entityId;
}

export const elementDefaultSize: Record<MapElementType, { width: number; height: number }> = {
  TABLE: { width: 80, height: 80 },
  RESOURCE: { width: 110, height: 70 },
  WALL: { width: 240, height: 12 },
  DOOR: { width: 70, height: 12 },
  WINDOW: { width: 90, height: 10 },
  BAR: { width: 220, height: 60 },
  KITCHEN: { width: 200, height: 150 },
  CASHIER: { width: 110, height: 60 },
  BATHROOM: { width: 130, height: 110 },
  ENTRANCE: { width: 90, height: 16 },
  STAIRS: { width: 130, height: 90 },
  TEXT: { width: 140, height: 34 },
  CUSTOM: { width: 100, height: 100 },
};
